<template>
  <div class="brain-search-admin" :class="{ 'brain-search-admin--full': fullWidth }">
    <v-card class="selector-card" elevation="2">
      <v-card-text>
        <div class="selector-header">
          <v-icon size="24" color="#EA5545" class="mr-2">mdi-file-search</v-icon>
          <div>
            <h3 class="selector-title">{{ t('brainSearchAdmin.selectKlarvertrag') }}</h3>
            <p class="selector-subtitle">
              Alle Brain-Suche-fähigen Klarverträge (prod_brain_search = 1)
            </p>
          </div>
        </div>
        <v-select
          v-model="internalSelectedId"
          :items="productOptions"
          item-title="label"
          item-value="value"
          variant="outlined"
          density="comfortable"
          placeholder="Klarvertrag auswählen…"
          :loading="isLoadingProducts"
          :disabled="isLoadingProducts"
          clearable
        >
          <template #prepend-inner>
            <v-icon size="20" color="#EA5545">mdi-package-variant</v-icon>
          </template>
        </v-select>
      </v-card-text>
    </v-card>

    <v-card class="search-card" elevation="2">
      <v-card-text>
        <div class="search-header">
          <div>
            <h3 class="search-title">{{ t('brainSearchAdmin.searchTitle') }}</h3>
            <p class="search-subtitle">
              Stelle Fragen zu jedem Klarvertrag – unabhängig vom Kundenkontext.
            </p>
          </div>
          <v-chip v-if="selectedProduct" size="small" color="#EA5545" variant="flat">
            ID {{ selectedProduct.prod_id }} · KLV {{ selectedProduct.prod_klv_id }}
          </v-chip>
        </div>

        <v-textarea
          v-model="searchBrainText"
          label="Frag den Klarvertrag"
          placeholder="Beschreibe deine Frage…"
          rows="4"
          variant="outlined"
          class="mb-4"
          :disabled="isSearching || !hasSelection"
        ></v-textarea>

        <v-btn
          color="#EA5545"
          size="large"
          block
          class="search-btn"
          :loading="isSearching"
          :disabled="!canSearch"
          @click="searchBrain"
        >
          <v-icon left class="mr-2">mdi-brain</v-icon>
          Klarvertrag durchsuchen
        </v-btn>

        <div v-if="brainResponse && !isSearching" class="response-card success">
          <div class="response-header">
            <v-icon color="#4caf50" class="mr-2" size="24">mdi-check-circle</v-icon>
            <span>{{ t('brainSearchAdmin.answer') }}</span>
          </div>
          <p class="response-text">{{ brainResponse }}</p>
          <v-btn variant="text" color="#EA5545" class="mt-2" size="small" @click="clearSearch">
            <v-icon left size="18">mdi-refresh</v-icon>
            Neue Frage
          </v-btn>
        </div>

        <div v-if="brainError && !isSearching" class="response-card error">
          <div class="response-header">
            <v-icon color="#d32f2f" class="mr-2" size="24">mdi-alert-circle</v-icon>
            <span>{{ t('brainSearchAdmin.error') }}</span>
          </div>
          <p class="response-text">{{ brainError }}</p>
          <v-btn variant="text" color="#d32f2f" class="mt-2" size="small" @click="clearSearch">
            <v-icon left size="18">mdi-refresh</v-icon>
            Erneut versuchen
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, inject, ref, watch } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

const { t } = useLanguage()

const props = defineProps({
  products: {
    type: Array,
    default: () => []
  },
  selectedProductId: {
    type: [Number, String, null],
    default: null
  },
  isLoadingProducts: {
    type: Boolean,
    default: false
  },
  fullWidth: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:selectedProductId', 'search-completed'])

const baseCalls = inject('baseCalls')

const internalSelectedId = ref(props.selectedProductId ?? null)
const searchBrainText = ref('')
const brainResponse = ref('')
const brainError = ref('')
const isSearching = ref(false)

watch(
  () => props.selectedProductId,
  value => {
    if (value !== internalSelectedId.value) {
      internalSelectedId.value = value ?? null
    }
  }
)

watch(internalSelectedId, value => {
  emit('update:selectedProductId', value ?? null)
  if (!value) {
    brainResponse.value = ''
    brainError.value = ''
  }
})

const hasSelection = computed(() => internalSelectedId.value !== null && internalSelectedId.value !== undefined)

const productOptions = computed(() => {
  return props.products.map(product => ({
    label: `${product.prod_name || 'Unbekannt'} · KLV ${product.prod_klv_id ?? '–'}`,
    value: product.prod_id
  }))
})

const selectedProduct = computed(() => {
  if (!hasSelection.value) {
    return null
  }
  const targetId = Number(internalSelectedId.value)
  if (Number.isNaN(targetId)) {
    return null
  }
  return props.products.find(product => Number(product.prod_id) === targetId) || null
})

const canSearch = computed(() => {
  return Boolean(
    hasSelection.value &&
      searchBrainText.value.trim() &&
      !isSearching.value
  )
})

async function searchBrain() {
  if (!canSearch.value || !selectedProduct.value) {
    return
  }

  brainResponse.value = ''
  brainError.value = ''
  isSearching.value = true

  try {
    const response = await baseCalls.clearmentPost({
      action: 'searchOpenaiVectorStoreTarife',
      prompt: searchBrainText.value,
      product_id: selectedProduct.value.prod_id
    })

    if (response.data.success) {
      brainResponse.value = response.data.result || response.data.payload
      emit('search-completed', {
        requestId: response.data.request_id ?? null,
        productId: selectedProduct.value.prod_id,
        prompt: searchBrainText.value,
        result: brainResponse.value
      })
    } else {
      brainError.value = response.data.error || response.data.message || 'Die Suche konnte nicht ausgeführt werden.'
    }
  } catch (error) {
    brainError.value = error.message || 'Die Suche ist fehlgeschlagen.'
  } finally {
    isSearching.value = false
  }
}

function clearSearch() {
  searchBrainText.value = ''
  brainResponse.value = ''
  brainError.value = ''
}
</script>

<style scoped src="@/assets/styles/sfc/components/objects/BrainSearchAdmin.css"></style>
